import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { useSite, useSeo, useFavorites } from "../../lib/site";
import { SiteLayout } from "../../components/site/SiteLayout";
import { SiteCard } from "../../components/site/SiteCard";
import { LeadForm } from "../../components/site/LeadForm";
import { Search, ArrowRight, Gem, Handshake, ShieldCheck } from "lucide-react";

const AREAS = ["Lisboa", "Cascais", "Sintra", "Porto", "Oeiras", "Algarve"];

function HeroSearch() {
  const { t, types } = useSite();
  const navigate = useNavigate();
  const [f, setF] = useState({ purpose: "venda", location: "", property_type: "", price_max: "" });
  const go = (e) => {
    e?.preventDefault();
    const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    navigate(`/propriedades?${q}`);
  };
  return (
    <form onSubmit={go} className="mt-10 bg-[#0D1B2A]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-3 sm:p-4 max-w-4xl" data-testid="hero-search">
      <div className="flex gap-2 mb-3">
        {["venda", "arrendamento"].map((p) => (
          <button type="button" key={p} onClick={() => setF({ ...f, purpose: p })} data-testid={`hero-purpose-${p}`}
            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${f.purpose === p ? "bg-[#C9A84C] text-[#0D1B2A]" : "text-slate-300 hover:text-white"}`}>{p === "venda" ? t("buy") : t("rent")}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-2">
        <input className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#C9A84C]" placeholder={t("location")} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="hero-location" />
        <select className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#C9A84C]" value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} data-testid="hero-type">
          <option value="" className="text-black">{t("any_type")}</option>
          {Object.entries(types).map(([k, l]) => <option key={k} value={k} className="text-black">{l}</option>)}
        </select>
        <input type="number" className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#C9A84C]" placeholder={`${t("max_price")} €`} value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value })} data-testid="hero-price" />
        <button type="submit" data-testid="hero-search-btn" className="inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#E0C574] text-[#0D1B2A] font-semibold rounded-xl px-6 py-3 transition-colors"><Search size={16} /> {t("search")}</button>
      </div>
    </form>
  );
}

function Counter({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let i = 0; const steps = 30;
    const id = setInterval(() => { i += 1; setN(Math.round((value * i) / steps)); if (i >= steps) clearInterval(id); }, 30);
    return () => clearInterval(id);
  }, [value]);
  return <span>{n}</span>;
}

export default function SiteHome() {
  const { t } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const { fav, toggle } = useFavorites();
  useSeo("Grupo Almar Investimentos | Imóveis em Portugal", t("hero_sub"));

  useEffect(() => { api.get("/public/properties", { params: { page_size: 6 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 })); }, []);
  useEffect(() => {
    if (location.hash) { setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300); }
  }, [location.hash]);

  const why = [[Gem, "why_1"], [Handshake, "why_2"], [ShieldCheck, "why_3"]];
  return (
    <SiteLayout>
      <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="site-hero">
        <motion.img src="/site/hero.jpg" alt="Lisboa" initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "easeOut" }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A] via-[#0D1B2A]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-16 w-full">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xs uppercase tracking-[0.4em] text-[#C9A84C]">{t("hero_kicker")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7 }} className="font-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-tight mt-4" data-testid="hero-title">{t("hero_title")}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-base sm:text-lg text-slate-300 max-w-xl mt-5">{t("hero_sub")}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}><HeroSearch /></motion.div>
          <div className="flex flex-wrap gap-2 mt-6">
            {AREAS.map((a) => <button key={a} onClick={() => navigate(`/propriedades?location=${a}`)} data-testid={`area-chip-${a}`} className="text-xs text-slate-200 border border-white/20 hover:border-[#C9A84C] hover:text-[#E0C574] rounded-full px-4 py-1.5 transition-colors">{a}</button>)}
          </div>
          {data && <p className="mt-8 text-sm text-slate-300" data-testid="published-count"><span className="font-display text-3xl text-[#E0C574] mr-2"><Counter value={data.total} /></span>{t("published_count")}</p>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="featured-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div><h2 className="font-display text-3xl sm:text-4xl text-white">{t("featured")}</h2><p className="text-slate-400 mt-2">{t("featured_sub")}</p></div>
          <button onClick={() => navigate("/propriedades")} data-testid="see-all-btn" className="inline-flex items-center gap-2 text-[#E0C574] hover:gap-3 transition-all">{t("see_all")} <ArrowRight size={16} /></button>
        </div>
        {data && data.items.length === 0 && <p className="text-slate-400" data-testid="no-featured">{t("no_results")}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data?.items.map((p, i) => <SiteCard key={p.id} p={p} i={i} fav={fav.includes(p.id)} onFav={toggle} />)}
        </div>
      </section>

      <section className="bg-[#F6F1E7] text-[#0D1B2A]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.img initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} src="/site/interior.jpg" alt="" className="rounded-3xl w-full aspect-[4/3] object-cover shadow-2xl" />
          <div>
            <h2 className="font-display text-3xl sm:text-4xl">{t("why_title")}</h2>
            <div className="mt-8 space-y-7">
              {why.map(([I, k], i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="flex gap-4">
                  <span className="w-12 h-12 rounded-2xl bg-[#0D1B2A] text-[#C9A84C] flex items-center justify-center flex-shrink-0"><I size={20} /></span>
                  <div><p className="font-semibold text-lg">{t(k)}</p><p className="text-sm text-slate-600 mt-1">{t(`${k}d`)}</p></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="avaliacao" className="relative overflow-hidden" data-testid="valuation-section">
        <img src="/site/night.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0D1B2A]/85" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#C9A84C]">{t("nav_sell")}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-3">{t("val_title")}</h2>
            <p className="text-slate-300 mt-4 max-w-md">{t("val_sub")}</p>
          </div>
          <div className="bg-[#0D1B2A]/70 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8">
            <LeadForm kind="avaliacao" testPrefix="valuation" />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
